import { getLogicalExpression, getFilterExpression } from '.';
import { axios } from '../internal';
const CURRENT_USER_ID = '@currentUser';
const getBaseUrl = (id, type) => {
  return `/identities/${type === 'user' ? 'users' : 'groups'}/${id}`;
};

/**
 * @returns a Promise that resolves to the identity info about the given id and type.
 */
export const getIdentity = async function getIdentity() {
  let id = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : CURRENT_USER_ID;
  let type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'user';
  const {
    data
  } = await axios.get(getBaseUrl(id, type), {
    headers: {
      Accept: `application/vnd.sas.identity.${type}+json`
    }
  });
  return data;
};

/**
 * @returns a Promise that resolves to the avatar info about the given id and type.
 */
export const getIdentityAvatar = async function getIdentityAvatar() {
  let id = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : CURRENT_USER_ID;
  let type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'user';
  const {
    data
  } = await axios.get(`${getBaseUrl(id, type)}/avatar`, {
    headers: {
      Accept: 'application/vnd.sas.identity.avatar+json'
    }
  });
  return data;
};

/**
 * @returns the <code>src</code> of the avatar's custom image.
 */
export const getIdentityAvatarSrc = function getIdentityAvatarSrc() {
  let id = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : CURRENT_USER_ID;
  let type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'user';
  return `${getBaseUrl(id, type)}/avatar/content`;
};
const createFilterString = (filterValue, idFilterValue, dataType) => {
  let filterString = '';
  if (idFilterValue === undefined) {
    filterString = filterValue.length === 0 ? '' : getLogicalExpression('or', getFilterExpression('contains', '$primary,name', filterValue), getFilterExpression('contains', '$primary,id', filterValue));
  } else {
    const nameFilterValue = filterValue.split('(').slice(0, -1).join('(').trim();
    filterString = `${getLogicalExpression('or', getFilterExpression('contains', '$primary,name', filterValue), getLogicalExpression('and', getFilterExpression('contains', '$primary,name', nameFilterValue), getFilterExpression('startsWith', '$primary,id', idFilterValue)))}`;
  }
  if (dataType === 'customGroupsOnly' || dataType === 'predefinedGroupsOnly') {
    const op = dataType === 'customGroupsOnly' ? 'eq' : 'ne';
    filterString = filterString.length > 0 ? getLogicalExpression('and', filterString, getFilterExpression(op, 'providerId', 'local')) : getFilterExpression(op, 'providerId', 'local');
  }
  return filterString.length > 0 ? `&filter=${filterString}` : '';
};

/**
 * @param filterValue Only the identities, of which the id or the name contains this value, would be returned.
 * @param idFilterValue optional extra filter value to allow name and id to be filtered differently.  Falls back on filterValue.
 * @returns a Promise that resolves to a collection of identities.
 */
export const getIdentityList = async (startIndex, limit, filterValue, dataType, idFilterValue) => {
  const filter = createFilterString(filterValue, idFilterValue, dataType);
  const getType = () => {
    switch (dataType) {
      case 'usersOnly':
        return 'users';
      case 'groupsOnly':
      case 'customGroupsOnly':
      case 'predefinedGroupsOnly':
        return 'groups';
      case 'usersAndGroups':
        return 'identities';
    }
  };
  const url = `/identities/${getType()}?start=${startIndex}&limit=${limit}&sortBy=name%3Aprimary${filter}&_=${Date.now()}`;
  try {
    const {
      data
    } = await axios.get(url, {
      headers: {
        Accept: 'application/vnd.sas.collection+json'
      }
    });
    return data;
  } catch (e) {
    throw new Error(`Failed to fetch user/group list ${url}`);
  }
};

/**
 * @param filterValues Array of ids to retrieve identities for.
 * @returns a Promise that resolves to a collection of identities.
 */
export const getIdentityListByIds = async (startIndex, limit, filterValues, dataType) => {
  const filter = filterValues.length > 0 ? `&filter=${getFilterExpression('in', 'id', ...filterValues)}` : '';
  const type = dataType === 'usersOnly' ? 'users' : 'groups';
  const url = `/identities/${type}?start=${startIndex}&limit=${limit}&sortBy=name%3Aascending${filter}&_=${Date.now()}`;
  try {
    const {
      data
    } = await axios.get(url, {
      headers: {
        Accept: 'application/vnd.sas.collection+json'
      }
    });
    return data;
  } catch (e) {
    throw new Error(`Failed to fetch user/group list by id ${url}`);
  }
};
export const saveAvatar = async function saveAvatar(contentType, body) {
  let id = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : CURRENT_USER_ID;
  let type = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 'user';
  await axios.put(`${getBaseUrl(id, type)}/avatar`, body, {
    headers: {
      Accept: 'application/vnd.sas.identity.avatar+json',
      'Content-Type': contentType
    }
  });
};
export const deleteAvatar = async function deleteAvatar() {
  let id = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : CURRENT_USER_ID;
  let type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'user';
  await axios.delete(`${getBaseUrl(id, type)}/avatar`, {
    headers: {
      Accept: 'application/vnd.sas.identity.avatar+json'
    }
  });
};
export const hasElevatedPrivileges = async function hasElevatedPrivileges() {
  let id = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : CURRENT_USER_ID;
  let type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'user';
  const {
    data
  } = await axios.get(`${getBaseUrl(id, type)}/administrator`, {
    headers: {
      Accept: 'application/vnd.sas.primitive.boolean+json'
    }
  });
  return data;
};

/**
 * @param filterValues Array of ids to retrieve identities for.
 * @returns a Promise that resolves to a collection of identities.
 */
export const getFilterIdentityListByIds = async (filterValues, dataType) => {
  const filter = filterValues.length > 0 ? `filter=${getFilterExpression('in', 'id', ...filterValues)}` : '';
  const type = dataType === 'usersOnly' ? 'users' : 'groups';
  const url = `/identities/${type}?${filter}`;
  try {
    const {
      data
    } = await axios.get(url, {
      headers: {
        Accept: 'application/vnd.sas.collection+json'
      }
    });
    return data;
  } catch (e) {
    throw new Error(`Failed to fetch user/group list by id ${url}`);
  }
};
//# sourceMappingURL=identities.js.map